import Line from '../canvas/line'
import { IndicatorType } from '../constants/common'
import { getFirstBarToRenderIndex } from '../utils/draw_in_visible_area'
import { getParsedIntegersFromPeriodString } from '../utils/helpers'
import Indicator from './indicator'

interface Attrs {
  period: string
}

class Stochastics<T extends Attrs = Attrs> extends Indicator<T> {
  static type = IndicatorType.Stofu
  static label = 'Stochastics FULL'

  k: number[] = []
  k1: number[] = []
  d: number[] = []
  declare kPeriod: number
  declare period: number
  declare dPeriod: number

  set(values: Partial<T>) {
    super.set(values)
    if (typeof values.period === 'string' && this.kPeriod === undefined) {
      const [period = 0, kPeriod = 0, dPeriod = 0] = getParsedIntegersFromPeriodString(values.period)
      this.period = period
      this.kPeriod = kPeriod
      this.dPeriod = dPeriod
    }
    this.trigger('change')
  }

  compute() {
    if (!this.isComputeNecessary()) return

    const period = Math.min(this.period, this.data.close.length - 1)
    let dCount, dSum, k
    this.k1 = []
    this.k = []
    this.d = []
    let kSum = (dSum = 0)
    let kCount = (dCount = 0)
    for (let i = 0; i < this.data.close.length; i++) {
      let highestHigh = this.data.high[i]
      let lowestLow = this.data.low[i]

      for (let j = i - period + 1; j <= i; j++) {
        if (this.data.high[j] > highestHigh) {
          highestHigh = this.data.high[j]
        }
        if (this.data.low[j] < lowestLow) {
          lowestLow = this.data.low[j]
        }
      }

      k = ((this.data.close[i] - lowestLow) / (highestHigh - lowestLow)) * 100
      if (highestHigh === lowestLow) {
        k = 100
      }
      this.k1[i] = k
      if (this.kPeriod > 1) {
        kSum += k
        kCount++
        if (kCount > this.kPeriod) {
          kSum -= this.k1[i - this.kPeriod]
          kCount--
        }
        k = kSum / kCount
      }

      dSum += k
      dCount++
      if (dCount > this.dPeriod) {
        dSum -= this.k[i - this.dPeriod]
        dCount--
      }

      const d = dSum / dCount
      this.k[i] = k
      this.d[i] = d
    }

    this.lastValue = this.k.last() ?? null
    const { min, max } = this.getDomainDefaults(this.type)
    this.min = min
    this.max = max
  }

  renderIndicator(context: CanvasRenderingContext2D) {
    const firstBarToRender = getFirstBarToRenderIndex(this.data.close, this.period, this.fx, this.leftOffset)
    if (firstBarToRender === undefined) return
    let lastBarToRender = 0

    new Line(
      {
        x1: -this.leftOffset,
        x2: -this.leftOffset + this.contentWidth,
        y1: Math.round(this.fy(50)),
        y2: Math.round(this.fy(50)),
        strokeStyle: '#ff8787',
        dashLength: 3,
      },
      this.model
    ).render(context)
    new Line(
      {
        x1: -this.leftOffset,
        x2: -this.leftOffset + this.contentWidth,
        y1: Math.round(this.fy(20)),
        y2: Math.round(this.fy(20)),
        strokeStyle: '#69c1ea',
      },
      this.model
    ).render(context)
    new Line(
      {
        x1: -this.leftOffset,
        x2: -this.leftOffset + this.contentWidth,
        y1: Math.round(this.fy(80)),
        y2: Math.round(this.fy(80)),
        strokeStyle: '#d386df',
      },
      this.model
    ).render(context)

    context.set('strokeStyle', this.getChartLayoutSettings().IndicatorSettings.general.Colors.line)
    context.translate(0.5, 0.5)
    context.beginPath()
    for (let i = firstBarToRender; i < this.data.close.length; i++) {
      const x = this.fx(i)
      if (x + this.leftOffset > this.width + 10) {
        break
      }
      context.lineTo(x, Math.round(this.fy(this.k[i])))
      lastBarToRender = i
    }
    context.stroke()

    context.set('strokeStyle', '#c80000')
    context.beginPath()
    for (let i = firstBarToRender; i <= lastBarToRender; i++) {
      context.lineTo(this.fx(i), Math.round(this.fy(this.d[i])))
    }
    context.stroke()
    context.translate(-0.5, -0.5)
  }

  getModalConfig() {
    const period = {
      type: 'number',
      label: 'Period',
      name: 'period',
      value: this.period ?? 14,
      required: true,
      min: 1,
      max: 999999,
    }
    const kPeriod = {
      type: 'number',
      label: 'K Period',
      name: 'kPeriod',
      value: this.kPeriod ?? 3,
      required: true,
      min: 1,
      max: 999999,
    }
    const dPeriod = {
      type: 'number',
      label: 'D Period',
      name: 'dPeriod',
      value: this.dPeriod ?? 3,
      required: true,
      min: 1,
      max: 999999,
    }

    return {
      title: this.label,
      inputs: [period, kPeriod, dPeriod],
      inputsErrorMessages: {
        period: `${period.label} must be a whole number between ${period.min} and ${period.max}`,
        dPeriod: `${dPeriod.label} must be a whole number between ${dPeriod.min} and ${dPeriod.max}`,
        kPeriod: `${kPeriod.label} must be a whole number between ${kPeriod.min} and ${kPeriod.max}`,
      } as {
        period: string
        dPeriod: string
        kPeriod?: string
      },
    }
  }

  getIsValid(key: string): boolean {
    switch (key) {
      case 'period':
      case 'dPeriod':
      case 'kPeriod':
        return this.getIsNumberInputValid({ key })
      default:
        return false
    }
  }

  toString() {
    return `Stochastics FULL ${this.period},${this.kPeriod},${this.dPeriod}`
  }
}

export default Stochastics
